import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { advancedHostingActions } from '@/redux/modules/advancedHosting';

/**
 * Hook que busca e retorna o status formatado de um servidor específico.
 * Cada servidor tem seu próprio estado no Redux, evitando sobrescrita.
 * Funciona tanto na listagem quanto na página de detalhes.
 *
 * @param {Object} serverInfo - Informações do servidor, usadas para status locked/pending/suspended/building/offline
 * @param {string|number} serverId - ID do servidor / ID do Hosting
 * @returns {{
 *   status: { value: string, variant: string } | null,
 *   loading: boolean,
 *   hasError: boolean,
 *   refetch: Function,
 * }}
 */
const useServerStatus = (serverInfo, serverId) => {
  const dispatch = useDispatch();
  const hasFetched = useRef({});

  // Função para forçar refetch
  const refetch = () => {
    if (serverId) {
      hasFetched.current[serverId] = false;
      dispatch(advancedHostingActions.serverStatus.request({ serverId }));
    }
  };

  // Busca o status específico deste servidor do estado mapeado
  const serverStatusState = useSelector(
    state => state.advancedHosting?.serverStatus?.byId?.[serverId],
  );

  const serverIsLocked = serverInfo?.locked;
  const serverIsLockedReason = serverInfo?.reason;
  const serverDisabledFeatures = serverIsLocked || serverStatusState?.status === 'offline';

  const serverStatus = serverIsLocked ? serverIsLockedReason : serverStatusState?.status;
  const serverStatusLoading = serverIsLocked ? false : serverStatusState?.loading;
  const hasError = serverIsLocked ? false : serverStatusState?.error;


  // Dispara a requisição apenas uma vez por serverId
  // Se houver erro, não tenta novamente até um refetch manual
  useEffect(() => {
    if (
      serverId
      && !hasFetched.current[serverId]
      && !serverStatusLoading
      && !serverStatus
      && !hasError
      && !serverIsLocked // Se o servidor estiver locked, não busca status do WHMCS, pois o status é derivado do reason
    ) {
      hasFetched.current[serverId] = true;
      dispatch(advancedHostingActions.serverStatus.request({ serverId }));
    }
  }, [dispatch, serverId, serverStatusLoading, serverStatus, hasError, serverIsLocked]);

  return {
    serverStatus,
    serverStatusLoading,
    serverIsLocked,
    serverDisabledFeatures,
    hasError,
    refetch,
  };
};

export default useServerStatus;
